import React, { useState } from "react";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useApiMutation } from "src/hooks/useApiMutation";
import type {
	ISessionRequest,
	IUpdateSessionRequestPayload,
} from "src/Types/module/b2bSession/b2bSessionRequest.types";

interface Props {
	open: boolean;
	handleClose: () => void;
	request: ISessionRequest;
}

const UpdateSessionRequestModal: React.FC<Props> = ({
	open,
	handleClose,
	request,
}) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

	const [status, setStatus] = useState<string>("approved");
	const [formError, setFormError] = useState<string | null>(null);

	const { mutate: updateRequest, isPending } = useApiMutation<
		IUpdateSessionRequestPayload,
		any
	>({
		method: "PATCH",
		endPoint: `/api/fair/admin/sessions/requests/${request.id}`,
		invalidateQueries: ["SessionRequests", "SessionRequest"],
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setFormError(null);

		const payload: IUpdateSessionRequestPayload = {
			status: status || "approved",
		};

		updateRequest(payload, {
			onSuccess: () => {
				handleClose();
			},
			onError: (err: any) => {
				console.error(err);
				setFormError(err?.message || "Failed to update session request.");
			},
		});
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullScreen={fullScreen}
			maxWidth="xs"
			fullWidth
		>
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 3,
					py: 2,
				}}
			>
				<Typography variant="h5" fontWeight={700}>
					Update Session Request
				</Typography>
				<IconButton onClick={handleClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<Divider />

			<Box component="form" onSubmit={handleSubmit}>
				<DialogContent sx={{ p: 3 }}>
					{formError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{formError}
						</Alert>
					)}

					<Grid container spacing={2}>
						{/* Status */}
						<Grid item xs={12}>
							<FormControl fullWidth size="small">
								<InputLabel id="edit-status-label" shrink>
									Status
								</InputLabel>
								<Select
									labelId="edit-status-label"
									value={status}
									label="Status"
									notched
									onChange={(e) => setStatus(e.target.value)}
								>
									<MenuItem value="approved">Approved</MenuItem>
								</Select>
							</FormControl>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button variant="outlined" onClick={handleClose} disabled={isPending}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={isPending}>
						{isPending ? "Saving..." : "Save Changes"}
					</Button>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default UpdateSessionRequestModal;
